import { Row, Col, message, Table, Pagination, Select, Tag, Input } from "antd";
import styled from "styled-components";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdVisibility } from "react-icons/md";
import { NavLink, useSearchParams } from "react-router-dom";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { getSocietyAmendments } from "../../store/admin-slice/adminServicesSlice";
import { Amendment } from "common/interface/Amendment";
import { routes } from "common/routes";
import { debounce } from "common/utils";

type AmendmentRow = Amendment & { key: string };

const MESSAGE_KEY = "society_amendments";

const headerKeys: { [key: string]: string } = {
    id: "Request ID",
    requestType: "Request Type",
    entityId: "Society ID",
    status: "Status",
    requestedBy: "Requested By",
    requestedAt: "Requested At",
    actions: "Actions",
};

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const statusColorMap: Record<string, string> = {
    PENDING: "gold",
    APPROVED: "green",
    REJECTED: "red",
    CANCELLED: "default",
};

const ApproveSocietyListingPage = () => {
    useDocumentTitle("Society Requests");
    const dispatch = useAppDispatch();
    const [columns, setColumns] = useState<ColumnsType<AmendmentRow>>();
    const [rows, setRows] = useState<AmendmentRow[]>();
    const { amendments, amendmentStatus, amendmentError } = useAppSelector((state) => state.adminServices);
    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = searchParams.get("status") ?? "PENDING";
    const requestTypeFilter = searchParams.get("requestType") ?? "";
    const entityIdFilter = searchParams.get("entityId") ?? "";
    const [entityIdInput, setEntityIdInput] = useState(entityIdFilter);

    useEffect(() => {
        setEntityIdInput(entityIdFilter);
    }, [entityIdFilter]);

    const updateEntityIdFilter = useCallback((value: string) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (value) {
                params.set("entityId", value);
            } else {
                params.delete("entityId");
            }
            return params;
        });
    }, [setSearchParams]);

    const debouncedUpdateEntityId = useMemo(
        () => debounce((value: string) => updateEntityIdFilter(value.trim()), 400),
        [updateEntityIdFilter]
    );

    const getData = useCallback(
        (pageNum = 1, pageSize = 25) => {
            dispatch(getSocietyAmendments({
                pageNo: pageNum,
                pageSize,
                status: statusFilter || undefined,
                requestType: requestTypeFilter || undefined,
                entityId: entityIdFilter || undefined,
            }));
        },
        [dispatch, statusFilter, requestTypeFilter, entityIdFilter]
    );

    useEffect(() => {
        getData(1, 25);
    }, [getData]);

    useEffect(() => {
        if (amendmentStatus === "loading") {
            message.loading({ content: "Loading", key: MESSAGE_KEY });
        } else if (amendmentStatus === "error" && amendmentError) {
            message.error({ content: amendmentError, key: MESSAGE_KEY });
        } else {
            message.destroy(MESSAGE_KEY);
        }
    }, [amendmentStatus, amendmentError]);

    useEffect(() => {
        const data: AmendmentRow[] = (amendments?.data ?? []).map((item, i) => ({
            ...item,
            key: `amendment-${item.id}-${i}`,
        }));

        const tableHeaders: ColumnsType<AmendmentRow> = Object.keys(headerKeys).map((key) => ({
            key,
            title: headerKeys[key],
            dataIndex: key,
            render: (text: string, record: AmendmentRow) => {
                if (key === "status") {
                    return <Tag color={statusColorMap[record.status] ?? "default"}>{record.status}</Tag>;
                }

                if (key === "requestedAt") {
                    return record.requestedAt ? new Date(record.requestedAt).toLocaleString() : "-";
                }

                if (key === "entityId") {
                    return record.entityId ?? "-";
                }

                if (key === "actions") {
                    return (
                        <StyledActionLink to={`/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENT_DETAIL.replace(":id", String(record.id))}`}>
                            <MdVisibility />
                        </StyledActionLink>
                    );
                }

                return <>{text}</>;
            },
        }));

        setColumns(tableHeaders);
        setRows(data);
    }, [amendments]);

    const Paginator = () => {
        if (amendments && amendments.pageNo !== undefined) {
            const { pageNo, pageSize, total } = amendments;
            return (
                <StyledPagination
                    current={pageNo}
                    pageSize={pageSize}
                    total={total}
                    onChange={(page, size) => getData(page, size)}
                />
            );
        }
        return <></>;
    };

    const updateFilter = (key: string, value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set(key, value);
        } else {
            params.delete(key);
        }
        setSearchParams(params);
    };

    return (
        <>
            <Row>
                <Col>
                    <AppPageHeader title="Society Requests" />
                </Col>
            </Row>
            <Row>
                <AppActionStrip>
                    <div className="action-strip__item" style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
                        <Input
                            placeholder="Society ID"
                            allowClear
                            style={{ width: 160 }}
                            value={entityIdInput}
                            onChange={(e) => {
                                const value = e.target.value;
                                setEntityIdInput(value);
                                debouncedUpdateEntityId(value);
                            }}
                        />
                        <Select
                            value={statusFilter}
                            style={{ width: 160 }}
                            onChange={(value) => updateFilter("status", value)}
                            options={[
                                { value: "PENDING", label: "Pending" },
                                { value: "APPROVED", label: "Approved" },
                                { value: "REJECTED", label: "Rejected" },
                                { value: "CANCELLED", label: "Cancelled" },
                                { value: "", label: "All" },
                            ]}
                        />
                        <Select
                            value={requestTypeFilter || undefined}
                            allowClear
                            placeholder="Request Type"
                            style={{ width: 180 }}
                            onChange={(value) => updateFilter("requestType", value ?? "")}
                            options={[
                                { value: "CREATE", label: "Create" },
                                { value: "UPDATE", label: "Update" },
                                { value: "TOGGLE_ACTIVE", label: "Toggle Active" },
                            ]}
                        />
                    </div>
                </AppActionStrip>
            </Row>
            {rows && rows.length ? (
                <Row>
                    <Col xs={24}>
                        <Table
                            columns={columns}
                            dataSource={rows}
                            scroll={{ x: "100vh", y: "65vh" }}
                            size="small"
                            pagination={false}
                            loading={amendmentStatus === "loading"}
                            bordered
                        />
                    </Col>
                    <Col xs={24}>
                        <Paginator />
                    </Col>
                </Row>
            ) : (
                amendmentStatus !== "loading" && <>No society requests to show.</>
            )}
        </>
    );
};

export default ApproveSocietyListingPage;
